import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { DesignLibraryIcon } from "@v2/features/design-library/designLibraryIcon";
import { DeletePageModal } from "@v2/features/page-renderer/components/navbar/DeletePageModal";
import { UnpublishModal } from "@v2/features/page-renderer/components/navbar/UnpublishModal";
import { useCurrentFile } from "@v2/features/page-renderer/hooks/useCurrentFile";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useEditorStoreActions, usePanelView } from "@v2/store/editorStore";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { Bug, Copy, EyeOff, Settings, Settings2, Trash2 } from "lucide-react";

const PageSettingsMenu = () => {
  const { open: openDebugPanel } = useAdminPanelActions();
  const isDebugMode = useIsDebugMode();
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const currentFile = useCurrentFile();
  const isMobile = useIsMobile();
  const projectId = useCurrentProjectId();
  const [deletePageModalOpen, setDeletePageModalOpen] = React.useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = React.useState(false);

  const isPagePublished = Boolean(currentFile?.lastPublishedFileVersionId);

  const deletePageItem = (
    <MenuItem
      label="Delete Page"
      variant="destructive"
      startEnhancer={<Trash2 className="text-rose" />}
      disabled={isPagePublished}
      onClick={() => {
        if (currentFile) {
          setDeletePageModalOpen(true);
        }
      }}
    />
  );

  const { mutate: duplicatePage, isPending: isDuplicating } =
    nextJSProjectRouter.duplicatePage.useMutation({
      onSuccess: async ({ createdFile }) => {
        void nextJSProjectUtils.listPages.invalidate({ projectId });
        toast.success({
          title: "Page duplicated",
          description: `"${createdFile.name}" was created`,
        });
      },
      onError: () => {
        toast.error({
          title: "Failed to duplicate page",
          description: "Please try again or contact support@replo.app.",
        });
      },
    });

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Button
              icon
              variant="tertiary"
              size="base"
              UNSAFE_className={twMerge(
                (panelView === "designLibrary" ||
                  panelView === "pageSettings") &&
                  "bg-sky-soft text-sky border-sky-border hover:bg-sky-soft hover:text-sky hover:border-sky-border",
              )}
            >
              <Settings2
                className={twMerge(
                  "text-muted",
                  (panelView === "designLibrary" ||
                    panelView === "pageSettings") &&
                    "text-primary",
                )}
              />
            </Button>
          }
        />
        <MenuContent align="start" side="bottom" UNSAFE_className="w-[166px]">
          <MenuItem
            label="Page Settings"
            startEnhancer={<Settings />}
            selected={panelView === "pageSettings"}
            onClick={() => {
              setPanelView("pageSettings");
            }}
          />
          <MenuItem
            label="Design Library"
            startEnhancer={<DesignLibraryIcon />}
            selected={panelView === "designLibrary"}
            onClick={() => {
              setPanelView("designLibrary");
            }}
          />
          <MenuItem
            label="Duplicate Page"
            startEnhancer={<Copy />}
            disabled={isDuplicating}
            onClick={() => {
              if (projectId && currentFile) {
                duplicatePage({
                  projectId,
                  fileId: currentFile.id,
                });
              }
            }}
          />
          {isMobile && isDebugMode && (
            <MenuItem
              label="Open Debug Panel"
              startEnhancer={<Bug />}
              onClick={() => {
                openDebugPanel();
              }}
            />
          )}
          {isPagePublished && (
            <MenuItem
              label="Unpublish Page"
              startEnhancer={<EyeOff />}
              onClick={() => {
                if (currentFile) {
                  setUnpublishModalOpen(true);
                }
              }}
            />
          )}
          <MenuSeparator />
          {isPagePublished ? (
            <Tooltip content="Unpublish this page to delete" side="right">
              {deletePageItem}
            </Tooltip>
          ) : (
            deletePageItem
          )}
        </MenuContent>
      </Menu>
      {currentFile && (
        <DeletePageModal
          open={deletePageModalOpen}
          onOpenChange={setDeletePageModalOpen}
          page={currentFile}
        />
      )}
      {currentFile && (
        <UnpublishModal
          open={unpublishModalOpen}
          onOpenChange={setUnpublishModalOpen}
          type="page"
          page={currentFile}
        />
      )}
    </>
  );
};

export default PageSettingsMenu;
